import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { getUserGymId } from "@/utils/gymAuth";
import {
  getInvoiceConfig,
  saveInvoiceConfig,
  LEGACY_INVOICE_CONFIG,
  type InvoiceConfig,
} from "@/services/invoiceConfigService";

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export function InvoicingTab() {
  const { t } = useTranslation(["settings", "common"]);
  const [config, setConfig] = useState<InvoiceConfig>(LEGACY_INVOICE_CONFIG);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [loadFailed, setLoadFailed] = useState(false);

  useEffect(() => {
    let mounted = true;
    const load = async () => {
      const gymId = getUserGymId();
      if (!gymId) {
        if (mounted) {
          setIsLoading(false);
          setLoadFailed(true);
        }
        return;
      }
      try {
        const loaded = await getInvoiceConfig(gymId);
        if (mounted) {
          setConfig(loaded);
          setLoadFailed(false);
        }
      } catch (error) {
        console.error("[InvoicingTab] Chargement impossible:", error);
        if (mounted) setLoadFailed(true);
      } finally {
        if (mounted) setIsLoading(false);
      }
    };
    load();
    return () => {
      mounted = false;
    };
  }, []);

  const update = (field: keyof InvoiceConfig, value: string) =>
    setConfig((current) => ({ ...current, [field]: value }));

  const handleSave = async () => {
    const gymId = getUserGymId();
    if (!gymId) {
      toast.error(t("invoicing.noGym"));
      return;
    }
    if (!config.companyName.trim()) {
      toast.error(t("invoicing.companyNameRequired"));
      return;
    }
    if (config.recipientEmail.trim() && !EMAIL_REGEX.test(config.recipientEmail.trim())) {
      toast.error(t("invoicing.invalidEmail"));
      return;
    }

    setIsSaving(true);
    try {
      await saveInvoiceConfig(gymId, config);
      toast.success(t("common:saveSuccess"));
    } catch (error) {
      console.error("[InvoicingTab] Enregistrement impossible:", error);
      toast.error(t("common:saveFailed"));
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>{t("invoicing.issuerTitle")}</CardTitle>
          <CardDescription>{t("invoicing.issuerDescription")}</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {isLoading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          ) : (
            <>
              {loadFailed && (
                <p className="text-sm text-destructive">{t("invoicing.loadError")}</p>
              )}

              <div className="space-y-2">
                <Label htmlFor="invoice-company-name">{t("invoicing.companyName")} *</Label>
                <Input
                  id="invoice-company-name"
                  value={config.companyName}
                  onChange={(e) => update("companyName", e.target.value)}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="invoice-company-address">{t("invoicing.companyAddress")}</Label>
                <Textarea
                  id="invoice-company-address"
                  rows={2}
                  value={config.companyAddress}
                  onChange={(e) => update("companyAddress", e.target.value)}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="invoice-company-tax-id">{t("invoicing.companyTaxId")}</Label>
                <Input
                  id="invoice-company-tax-id"
                  value={config.companyTaxId}
                  onChange={(e) => update("companyTaxId", e.target.value)}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="invoice-recipient-email">{t("invoicing.recipientEmail")}</Label>
                <Input
                  id="invoice-recipient-email"
                  type="email"
                  placeholder="compta@exemple.com"
                  value={config.recipientEmail}
                  onChange={(e) => update("recipientEmail", e.target.value)}
                />
                <p className="text-xs text-muted-foreground">
                  {t("invoicing.recipientEmailHelp")}
                </p>
              </div>

              <div className="flex justify-end">
                <Button onClick={handleSave} disabled={isSaving}>
                  {isSaving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                  {t("common:save")}
                </Button>
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
